// ReportTable — таблица результата отчёта (V14 W9, ТЗ §3.6): один компонент на все 7 отчётов
// (композиция ТЗ: ReportTable.tsx, лимит ≤10 прод-файлов волны). Секции R3 (revenue):
// «Оплачено (факт)» / «Закрыто сделок (факт)» / «Прогноз» — раздельные подписи, общий
// «итог» НЕ выводится (I4, двойной счёт). Стадии/источники — существующие ключи
// (crm.deals.stage.*, leads.source.*); риск-причины — reports.riskReason.* (W10: значения).

import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { formatDate, formatDateOnly, formatLeadSource, formatStage } from '@/lib/format';
import { t } from '@/i18n';
import type {
  CurrencyTotal,
  FunnelRow,
  LeadConversionRow,
  ManagersRow,
  MonthlyRow,
  ReportDescriptor,
  ReportResponse,
  RefusalsRow,
  RevenueRow,
  RiskRow,
  StageTotal,
} from '@/api/reports';

/** Формат суммы: Intl.NumberFormat ru-RU «1 234 567,89» (ТЗ §3.6) + код валюты. */
function money(amount: number, currency: string): string {
  const formatted = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(amount);
  return `${formatted} ${currency}`;
}

function headerCell(label: string, index: number): JSX.Element {
  return (
    <TableCell key={`${label}-${index}`}>
      <Typography variant="subtitle2">{label}</Typography>
    </TableCell>
  );
}

function bodyRow(key: string, cells: JSX.Element[]): JSX.Element {
  return <TableRow key={key}>{cells}</TableRow>;
}

/** Полная таблица «заголовки + строки» (для отчёта целиком и для секций R3). */
function SimpleTable({ headers, rows }: { headers: string[]; rows: JSX.Element[] }): JSX.Element {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>{headers.map((header, index) => headerCell(header, index))}</TableRow>
      </TableHead>
      <TableBody>
        {rows.length > 0 ? (
          rows
        ) : (
          <TableRow>
            <TableCell colSpan={headers.length}>—</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

/** Секция R3 «Выручка»: подпись + таблица (шапка пустой секции выводится всегда — BЭ-канон). */
function RevenueSection({
  title,
  headers,
  rows,
}: {
  title: string;
  headers: string[];
  rows: JSX.Element[];
}): JSX.Element {
  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600 }}>
        {title}
      </Typography>
      <SimpleTable headers={headers} rows={rows} />
    </Box>
  );
}

/** Секция R3: столбцы «Валюта | Кол-во | Сумма» (payments/wonDeals). */
function currencySection(title: string, totals: CurrencyTotal[]): JSX.Element {
  return (
    <RevenueSection
      title={title}
      headers={[t('reports.currency'), t('reports.count'), t('reports.amount')]}
      rows={totals.map((total) =>
        bodyRow(`${title}-${total.currency}`, [
          <TableCell key="currency">{total.currency}</TableCell>,
          <TableCell key="count">{total.count}</TableCell>,
          <TableCell key="amount">{money(total.amount, total.currency)}</TableCell>,
        ]),
      )}
    />
  );
}

/** Секция R3 «Прогноз»: стадия + валюта + кол-во + сумма (порядок воронки с BЭ). */
function forecastSection(stages: StageTotal[]): JSX.Element {
  return (
    <RevenueSection
      title={t('reports.report.revenue.sections.forecast')}
      headers={[t('reports.stage'), t('reports.currency'), t('reports.count'), t('reports.amount')]}
      rows={stages.map((stage) =>
        bodyRow(`${stage.stage}-${stage.currency}`, [
          <TableCell key="stage">{formatStage(stage.stage, t)}</TableCell>,
          <TableCell key="currency">{stage.currency}</TableCell>,
          <TableCell key="count">{stage.count}</TableCell>,
          <TableCell key="amount">{money(stage.amount, stage.currency)}</TableCell>,
        ]),
      )}
    />
  );
}

/** Таблица отчёта (Paper + TableContainer, канон PaymentsListPage). */
function ReportPaper({ children }: { children: JSX.Element }): JSX.Element {
  return (
    <Paper>
      <TableContainer>{children}</TableContainer>
    </Paper>
  );
}

interface ReportTableProps {
  report: ReportDescriptor;
  response: ReportResponse;
}

/** Таблица результата отчёта: R1 funnel / R2 конверсия / R3 revenue-секции / R4 / R5 / R6 / refusals. */
export function ReportTable({ report, response }: ReportTableProps): JSX.Element {
  const rows = response.rows;

  switch (report.id) {
    case 'funnel': {
      const funnelRows = rows as FunnelRow[];
      return (
        <ReportPaper>
          <SimpleTable
            headers={[t('reports.stage'), t('reports.currency'), t('reports.dealCount'), t('reports.amount')]}
            rows={funnelRows.map((row) =>
              bodyRow(`${row.stage}-${row.currency}`, [
                <TableCell key="stage">{formatStage(row.stage, t)}</TableCell>,
                <TableCell key="currency">{row.currency}</TableCell>,
                <TableCell key="count">{row.dealCount}</TableCell>,
                <TableCell key="amount">{money(row.amount, row.currency)}</TableCell>,
              ]),
            )}
          />
        </ReportPaper>
      );
    }
    case 'lead-conversion': {
      const conversionRows = rows as LeadConversionRow[];
      return (
        <ReportPaper>
          <SimpleTable
            headers={[t('reports.leadSource'), t('reports.totalCount'), t('reports.convertedCount'), t('reports.conversionPct')]}
            rows={conversionRows.map((row) =>
              bodyRow(row.source, [
                <TableCell key="source">{formatLeadSource(row.source, t)}</TableCell>,
                <TableCell key="total">{row.totalCount}</TableCell>,
                <TableCell key="converted">{row.convertedCount}</TableCell>,
                <TableCell key="pct">{row.convertedPct} %</TableCell>,
              ]),
            )}
          />
        </ReportPaper>
      );
    }
    case 'revenue': {
      const revenueRow = (rows as RevenueRow[])[0];
      // Пустой составной отчёт показывается на уровне страницы (EmptyState);
      // здесь — защитный fallback (строк не может быть 0: ReportTable вызывается при rows > 0).
      return (
        <ReportPaper>
          <Stack spacing={2} sx={{ p: 2 }}>
            {revenueRow ? (
              <>
                {currencySection(t('reports.report.revenue.sections.payments'), revenueRow.actual.payments)}
                {currencySection(t('reports.report.revenue.sections.wonDeals'), revenueRow.actual.wonDeals)}
                {forecastSection(revenueRow.forecast)}
              </>
            ) : (
              <SimpleTable headers={['—']} rows={[]} />
            )}
          </Stack>
        </ReportPaper>
      );
    }
    case 'by-managers': {
      const managerRows = rows as ManagersRow[];
      return (
        <ReportPaper>
          <SimpleTable
            headers={[t('reports.owner'), t('reports.department'), t('reports.currency'), t('reports.dealCount'), t('reports.activeAmount'), t('reports.wonCount'), t('reports.wonAmount')]}
            rows={managerRows.map((row) =>
              bodyRow(`${row.ownerUserId}-${row.currency}`, [
                <TableCell key="owner">{row.ownerName}</TableCell>,
                <TableCell key="department">{row.departmentName ?? '—'}</TableCell>,
                <TableCell key="currency">{row.currency}</TableCell>,
                <TableCell key="dealCount">{row.dealCount}</TableCell>,
                <TableCell key="activeAmount">{money(row.activeAmount, row.currency)}</TableCell>,
                <TableCell key="wonCount">{row.wonCount}</TableCell>,
                <TableCell key="wonAmount">{money(row.wonAmount, row.currency)}</TableCell>,
              ]),
            )}
          />
        </ReportPaper>
      );
    }
    case 'monthly-dynamics': {
      const monthlyRows = rows as MonthlyRow[];
      return (
        <ReportPaper>
          <SimpleTable
            headers={[t('reports.month'), t('reports.currency'), t('reports.dealsCreated'), t('reports.dealsCreatedAmount'), t('reports.wonCount'), t('reports.wonAmount'), t('reports.paymentsCount'), t('reports.paymentsAmount')]}
            rows={monthlyRows.map((row) =>
              bodyRow(`${row.month}-${row.currency}`, [
                <TableCell key="month">{formatDateOnly(row.month)}</TableCell>,
                <TableCell key="currency">{row.currency}</TableCell>,
                <TableCell key="created">{row.dealsCreatedCount}</TableCell>,
                <TableCell key="createdAmount">{money(row.dealsCreatedAmount, row.currency)}</TableCell>,
                <TableCell key="wonCount">{row.wonCount}</TableCell>,
                <TableCell key="wonAmount">{money(row.wonAmount, row.currency)}</TableCell>,
                <TableCell key="paymentsCount">{row.paymentsCount}</TableCell>,
                <TableCell key="paymentsAmount">{money(row.paymentsAmount, row.currency)}</TableCell>,
              ]),
            )}
          />
        </ReportPaper>
      );
    }
    case 'risks': {
      const riskRows = rows as RiskRow[];
      return (
        <ReportPaper>
          <SimpleTable
            headers={[t('reports.deal'), t('reports.stage'), t('reports.amount'), t('reports.currency'), t('reports.owner'), t('reports.department'), t('reports.nextContact'), t('reports.lastContact'), t('reports.inactiveDays'), t('reports.riskReason')]}
            rows={riskRows.map((row) =>
              bodyRow(row.dealId, [
                <TableCell key="title">{row.title}</TableCell>,
                <TableCell key="stage">{formatStage(row.stage, t)}</TableCell>,
                <TableCell key="amount">{money(row.amount, row.currency)}</TableCell>,
                <TableCell key="currency">{row.currency}</TableCell>,
                <TableCell key="owner">{row.ownerName}</TableCell>,
                <TableCell key="department">{row.departmentName ?? '—'}</TableCell>,
                <TableCell key="nextContact">{formatDate(row.nextContactAt)}</TableCell>,
                <TableCell key="lastContact">{formatDate(row.lastContactAt)}</TableCell>,
                <TableCell key="inactiveDays">{row.inactiveDays}</TableCell>,
                <TableCell key="riskReason">{t(`reports.riskReason.${row.riskReason}`)}</TableCell>,
              ]),
            )}
          />
        </ReportPaper>
      );
    }
    case 'refusals': {
      const refusalsRows = rows as RefusalsRow[];
      return (
        <ReportPaper>
          <SimpleTable
            headers={[t('reports.reasonName'), t('reports.dealCount'), t('reports.leadCount'), t('reports.total')]}
            rows={refusalsRows.map((row) =>
              bodyRow(row.reasonId ?? 'without-reason', [
                <TableCell key="reason">{row.reasonName}</TableCell>,
                <TableCell key="dealCount">{row.dealCount}</TableCell>,
                <TableCell key="leadCount">{row.leadCount}</TableCell>,
                <TableCell key="total">{row.dealCount + row.leadCount}</TableCell>,
              ]),
            )}
          />
        </ReportPaper>
      );
    }
  }
}
